<script setup lang="ts">
/**
 * About route.
 *
 * Deletable example content. Demonstrates a view that reads the health service — the pattern for
 * calling an API without a component ever touching `fetch` directly.
 *
 * The fetch is guarded to the client on purpose. This view is prerendered, and anything resolved
 * during the build is frozen into the static HTML: a status fetched at build time would claim the
 * service was healthy for as long as that deploy lives.
 *
 * The layer diagram is rendered from the same list the ESLint boundary rules describe. It is
 * deliberately plain markup rather than an image — a diagram nobody can read with a screen reader,
 * and which goes stale the moment a rule changes, is worse than an ordered list.
 */

/**
 * Vue
 */
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

/**
 * Libraries
 */
import { Activity, Layers, Sparkles, Waypoints } from '@lucide/vue'

/**
 * Components
 */
import AppBackdrop from '@/components/ui/AppBackdrop.vue'
import AppBadge from '@/components/ui/AppBadge.vue'
import AppCard from '@/components/ui/AppCard.vue'
import AppContainer from '@/components/ui/AppContainer.vue'
import AppSectionHeader from '@/components/ui/AppSectionHeader.vue'

/**
 * Config
 */
import { identityConfig } from '@/config/identity'

/**
 * Services
 */
import { api } from '@/services/client'

const { t } = useI18n()

const status = ref<string>('unknown')

/*
 * Three states, not two: a service that has not answered yet is not the same claim as one that
 * answered badly, and a dot that goes straight from grey to red says the wrong thing while the
 * request is still in flight.
 */
const statusTone = computed(() => {
  if (status.value === 'ok' || status.value === 'healthy') return 'text-primary'
  if (status.value === 'unknown') return 'text-muted-foreground'
  return 'text-destructive'
})

/** The layers, innermost first, in the order the boundary rules allow them to import each other. */
const layers = [
  { key: 'core', names: ['core', 'types', 'utils'] },
  { key: 'config', names: ['config'] },
  { key: 'services', names: ['services'] },
  { key: 'views', names: ['views', 'components'] }
] as const

onMounted(async () => {
  try {
    const response = await api.health.getStatus()
    status.value = response.data.status
  } catch {
    // A demo endpoint that is not running is not an error worth surfacing to a visitor.
    status.value = 'unavailable'
  }
})
</script>

<template>
  <section class="relative overflow-hidden">
    <AppBackdrop />

    <AppContainer class="relative py-20 md:py-24">
      <AppBadge class="enter font-mono uppercase tracking-[0.2em]" style="--enter-step: 0">
        <Sparkles class="h-3.5 w-3.5" aria-hidden="true" />
        {{ t('about.eyebrow') }}
      </AppBadge>

      <h1 class="mt-6 max-w-3xl font-headline text-4xl font-bold leading-[1.1] tracking-tight md:text-5xl">
        {{ t('about.title') }}
      </h1>

      <p class="enter mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground" style="--enter-step: 1">
        {{ t('about.lead') }}
      </p>
    </AppContainer>
  </section>

  <AppContainer as="section" aria-labelledby="foundations-heading" class="py-12 md:py-16">
    <AppSectionHeader
      id="foundations-heading"
      :eyebrow="t('about.foundations.eyebrow')"
      :title="t('about.foundations.title')"
    />

    <div class="mt-10 grid gap-5 md:grid-cols-2">
      <AppCard title="Identity" :heading-level="3" interactive class="reveal">
        <template #icon>
          <Waypoints class="h-5 w-5" aria-hidden="true" />
        </template>

        <p>{{ identityConfig.description }}</p>
        <p class="mt-3">
          Declared as a <strong class="font-semibold text-foreground">{{ identityConfig.type }}</strong> in
          <code class="rounded bg-muted px-1.5 py-0.5 font-code text-xs text-foreground">src/config/identity.ts</code>,
          which determines the entity type at the root of the structured-data graph.
        </p>

        <template #footer>
          <!--
            The topics the entity claims to know about, from the same array that becomes `knowsAbout`
            in the JSON-LD. Each is a reconciled topic with a `sameAs`, so the chip links out to the
            canonical page for the thing rather than asserting a bare string.
          -->
          <ul class="flex flex-wrap gap-2">
            <li v-for="topic in identityConfig.topics" :key="typeof topic === 'string' ? topic : topic.name">
              <AppBadge tone="outline">{{ typeof topic === 'string' ? topic : topic.name }}</AppBadge>
            </li>
          </ul>
        </template>
      </AppCard>

      <AppCard title="Service layer" :heading-level="3" interactive class="reveal" style="--reveal-step: 1">
        <template #icon>
          <Activity class="h-5 w-5" aria-hidden="true" />
        </template>

        <p>
          This card calls
          <code class="rounded bg-muted px-1.5 py-0.5 font-code text-xs text-foreground">api.health.getStatus()</code>
          after mount rather than during prerendering, so the value reflects now and not build time.
        </p>

        <template #footer>
          <!--
            `role="status"` makes this a live region: the value arrives after mount, and without it
            the change is silent to anyone using a screen reader.
          -->
          <p role="status" class="flex items-center gap-2 font-mono text-xs">
            <span class="relative flex h-2 w-2" :class="statusTone" aria-hidden="true">
              <span class="absolute inline-flex h-full w-full animate-ping rounded-full bg-current opacity-60" />
              <span class="relative inline-flex h-2 w-2 rounded-full bg-current" />
            </span>
            <span class="text-muted-foreground">Status</span>
            <span class="font-semibold text-foreground">{{ status }}</span>
          </p>
        </template>
      </AppCard>
    </div>
  </AppContainer>

  <AppContainer as="section" aria-labelledby="layers-heading" class="pb-24 pt-12">
    <AppSectionHeader id="layers-heading" :eyebrow="t('about.layers.eyebrow')" :title="t('about.layers.title')" />

    <ol class="mt-10 space-y-3">
      <li
        v-for="(layer, index) in layers"
        :key="layer.key"
        class="reveal flex flex-col gap-3 rounded-2xl border border-border bg-card/50 p-5 backdrop-blur-sm sm:flex-row sm:items-center sm:gap-6"
        :style="{ '--reveal-step': index }"
      >
        <div class="flex items-center gap-3 sm:w-56 sm:shrink-0">
          <Layers class="h-4 w-4 text-primary" aria-hidden="true" />
          <span class="font-headline text-sm font-semibold tracking-tight">{{ t(`about.layers.${layer.key}`) }}</span>
        </div>

        <ul class="flex flex-wrap gap-2">
          <li v-for="name in layer.names" :key="name">
            <AppBadge tone="muted" class="font-code">{{ name }}/</AppBadge>
          </li>
        </ul>
      </li>
    </ol>

    <p class="mt-6 max-w-2xl text-sm leading-relaxed text-muted-foreground">{{ t('about.layers.note') }}</p>
  </AppContainer>
</template>
